import React from 'react';
import { Link } from 'react-router-dom';
import CaseStudyGate from '../../components/CaseStudyGate';
import Footer from '../../components/Footer';
import OtherProjects from '../../components/OtherProjects';
import useNoIndex from '../../hooks/useNoIndex';

// Culture Amp purple, sampled directly from the logo/wordmark in the source deck.
const ACCENT = '#9A36B2';
// Muted mauve used for the research-flow diagram fills in the source deck.
const MAUVE = '#7D5881';
// Ink colors shared by every hand-sketch-style wireframe on this page, so the
// "Landed on" sketch and the rejected-direction wireframes read as one family.
const INK = '#3A3A3A';
const INK_LIGHT = '#A8A29E';
const SKETCH_BG = '#F5F3EC';

const SectionHeading = ({ eyebrow, title, intro }) => (
  <div className="mb-12">
    <p
      className="text-sm font-bold uppercase tracking-widest mb-3"
      style={{ color: ACCENT }}
    >
      {eyebrow}
    </p>
    <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">{title}</h2>
    {intro && <p className="text-tertiary text-lg max-w-2xl">{intro}</p>}
  </div>
);

// A screenshot presented with light browser chrome, a real shadow, and a caption
// explaining what it shows and why it matters.
const Shot = ({ src, alt, caption, step, chrome = true }) => (
  <figure className="flex flex-col">
    <div className="rounded-lg overflow-hidden shadow-xl border border-gray-light bg-white">
      {chrome && (
        <div className="flex items-center gap-1.5 px-3 py-2 bg-gray-light">
          <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
        </div>
      )}
      <img src={src} alt={alt} className="w-full h-auto block" />
    </div>
    <figcaption className="mt-3 text-sm text-tertiary">
      {step && (
        <span className="font-bold mr-1.5" style={{ color: ACCENT }}>
          {step}
        </span>
      )}
      {caption}
    </figcaption>
  </figure>
);

// One "Tested" vs. "Landed on" pair, with the feedback that drove the change.
// Shared frame for the recreated UI mockups below — same border, radius, and
// (via minHeight) the same footprint for every card in a Tested/Landed pair,
// so the two sides read as a deliberate before/after rather than two
// differently-sized screenshot crops.
const MockCard = ({ children, minHeight }) => (
  <div
    className="rounded-xl border border-gray-light bg-white p-6 flex flex-col justify-center"
    style={{ minHeight }}
  >
    {children}
  </div>
);

const KRPill = () => (
  <span
    className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium shrink-0"
    style={{ backgroundColor: '#DCF5E8', color: '#1A7A4C' }}
  >
    Key result
  </span>
);

// A restrained stand-in for a pointing arrow: a small dot, a short leader
// line, and a label — always in normal document flow, so it can never
// overflow or overlap the content around it.
const Callout = ({ label }) => (
  <div className="inline-flex items-center mt-3">
    <span className="text-xs font-medium" style={{ color: ACCENT }}>
      {label}
    </span>
  </div>
);

const KRSettingsTested = () => (
  <MockCard minHeight={230}>
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <KRPill />
      <span className="font-medium text-text-primary">I love working on this</span>
    </div>
    <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-xs">
      {['Add due date', 'Set success metric', 'Set owner', 'Add reminder'].map((l) => (
        <span key={l} className="font-medium" style={{ color: '#0168B3' }}>
          {l}
        </span>
      ))}
    </div>
  </MockCard>
);

const KRSettingsLanded = () => (
  <MockCard minHeight={230}>
    <div className="flex items-start justify-between gap-2 mb-2">
      <div className="flex flex-wrap items-center gap-2 bg-yellow-100 rounded px-2 py-1 -mx-2 flex-1">
        <KRPill />
        <span className="font-medium text-text-primary">Prove I understand user needs.</span>
      </div>
      <span className="text-sm tracking-widest shrink-0" style={{ color: '#0168B3' }}>•••</span>
    </div>
    <div className="flex justify-end">
      <div className="rounded-md border border-gray-light bg-white shadow-sm px-3 py-2 text-sm text-tertiary w-fit">
        <p className="py-0.5">Add due date</p>
        <p className="py-0.5">Add reminder</p>
        <p className="py-0.5">Add success metric</p>
      </div>
    </div>
    <div className="flex justify-end">
      <Callout label="Overflow menu, scoped to this row" />
    </div>
  </MockCard>
);

const DescriptionTested = () => (
  <MockCard minHeight={210}>
    {/* Ghost/placeholder text — kept at gray-400, same exception as the other
        recreated-UI colors, so it still reads as placeholder rather than
        real content or a page caption. */}
    <p className="text-xl text-gray-400 mb-4">What are you trying to achieve?</p>
    <div className="flex items-center gap-2">
      <KRPill />
      <span className="text-gray-400">To create a new key result press enter</span>
    </div>
  </MockCard>
);

const DescriptionLanded = () => (
  <MockCard minHeight={210}>
    <div className="flex items-center gap-2 mb-4">
      <KRPill />
      {/* Ghost/placeholder text — same gray-400 exception as above. */}
      <span className="text-gray-400">Add key results here, press enter to create a new key result</span>
    </div>
    <div className="rounded-lg border border-gray-light shadow-sm w-fit overflow-hidden mb-2">
      <div className="text-sm font-medium text-text-primary bg-gray-light px-3 py-1.5 border-b border-gray-light">
        + Add new
      </div>
      <div className="px-3 py-2 text-sm text-tertiary bg-white">
        <p className="py-0.5 flex gap-4">
          <span>Key result</span> <span className="text-gray-400">enter</span>
        </p>
        <p className="py-0.5 flex gap-4">
          <span>Task</span> <span className="text-gray-400">tab</span>
        </p>
        <p className="py-0.5 flex gap-4">
          <span>Paragraph</span> <span className="text-gray-400">enter + backspace</span>
        </p>
      </div>
    </div>
    <Callout label="Menu for people who don't use shortcuts" />
  </MockCard>
);

const TestedVsLanded = ({ tested, testedCaption, landed, landedCaption, feedback }) => (
  <div className="mb-20 last:mb-0">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
      <div className="flex flex-col">
        <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-3">Tested</p>
        {tested}
        <p className="mt-3 text-sm text-tertiary">{testedCaption}</p>
      </div>
      <div className="flex flex-col">
        <div className="flex items-center gap-2 mb-3">
          <p className="text-xs font-bold uppercase tracking-widest text-tertiary">
            Landed on
          </p>
          <span
            className="text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded-full text-white"
            style={{ backgroundColor: ACCENT }}
          >
            Shipped
          </span>
        </div>
        {landed}
        <p className="mt-3 text-sm text-text-primary font-medium">{landedCaption}</p>
      </div>
    </div>
    <p className="text-tertiary border-l-2 pl-4 text-sm" style={{ borderColor: ACCENT }}>
      {feedback}
    </p>
  </div>
);

const StatTile = ({ value, label }) => (
  <div>
    <p className="text-5xl sm:text-6xl font-bold mb-2" style={{ color: ACCENT }}>
      {value}
    </p>
    <p className="text-tertiary text-sm">{label}</p>
  </div>
);

// Recreation of a low-fi wireframe sketch, drawn as real SVG strokes (not a
// raster image) so it can animate itself in on scroll. This is the one
// intentional motion moment on the page — see the effect implementation
// below. Everything else on the page stays static.
const WireframeSketch = () => {
  const containerRef = React.useRef(null);
  const svgRef = React.useRef(null);
  const playedRef = React.useRef(false);

  // Hide every stroke before first paint so there's no flash of the fully
  // drawn sketch before the reveal plays. Skipped entirely under
  // prefers-reduced-motion, so that mode never depends on JS running.
  React.useLayoutEffect(() => {
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || !svgRef.current) return;
    try {
      svgRef.current.querySelectorAll('[data-stroke]').forEach((el) => {
        const len = el.getTotalLength();
        el.style.strokeDasharray = `${len}`;
        el.style.strokeDashoffset = `${len}`;
      });
      svgRef.current.querySelectorAll('[data-fade]').forEach((el) => {
        el.style.opacity = '0';
      });
    } catch {
      // If measuring fails for any reason, leave the sketch fully visible
      // rather than risk it getting stuck hidden.
    }
  }, []);

  React.useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const play = () => {
      if (playedRef.current) return;
      playedRef.current = true;

      const svg = svgRef.current;
      if (!svg) return;
      const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      const strokes = Array.from(svg.querySelectorAll('[data-stroke]'));
      const fades = Array.from(svg.querySelectorAll('[data-fade]'));

      if (prefersReduced) {
        strokes.forEach((el) => { el.style.strokeDashoffset = '0'; });
        fades.forEach((el) => { el.style.opacity = '1'; });
        return;
      }

      try {
        strokes.forEach((el, i) => {
          const len = el.getTotalLength();
          el.animate(
            [{ strokeDashoffset: len }, { strokeDashoffset: 0 }],
            { duration: 450, delay: i * 55, easing: 'ease-in-out', fill: 'forwards' }
          );
        });
        fades.forEach((el) => {
          const afterIndex = Number(el.dataset.fadeAfter || 0);
          el.animate(
            [{ opacity: 0 }, { opacity: 1 }],
            { duration: 300, delay: afterIndex * 55 + 450, easing: 'ease-out', fill: 'forwards' }
          );
        });
      } catch {
        strokes.forEach((el) => { el.style.strokeDashoffset = '0'; });
        fades.forEach((el) => { el.style.opacity = '1'; });
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            play();
            observer.disconnect();
          }
        });
      },
      { threshold: 0.35 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className="rounded-lg border border-gray-light shadow-lg overflow-hidden"
      style={{ backgroundColor: SKETCH_BG }}
    >
      <svg
        ref={svgRef}
        viewBox="0 0 1200 800"
        className="w-full h-auto block"
        role="img"
        aria-label="Low-fidelity wireframe sketch of the inline, list-based goal structure, with tab creating a task and backspace collapsing it to a paragraph"
      >
        <rect data-stroke x="60" y="90" width="1000" height="650" rx="16" fill="none" stroke={INK} strokeWidth="3" />

        <path data-stroke d="M100,150 C350,142 650,158 890,145" fill="none" stroke={INK} strokeWidth="5" strokeLinecap="round" />
        <path data-stroke d="M100,185 C220,182 320,188 430,184" fill="none" stroke={INK_LIGHT} strokeWidth="2.5" strokeLinecap="round" />
        <path data-stroke d="M100,255 C400,250 700,260 1000,253" fill="none" stroke={INK_LIGHT} strokeWidth="2" strokeLinecap="round" />

        <circle data-stroke cx="118" cy="308" r="8" fill="none" stroke={INK} strokeWidth="2.5" />
        <path data-stroke d="M145,308 C350,300 600,314 800,302" fill="none" stroke={INK} strokeWidth="3" strokeLinecap="round" />

        <circle data-stroke cx="118" cy="370" r="8" fill="none" stroke={INK} strokeWidth="2.5" />
        <path data-stroke d="M145,370 C320,362 520,376 700,364" fill="none" stroke={INK} strokeWidth="3" strokeLinecap="round" />

        <rect data-stroke x="148" y="408" width="28" height="28" rx="3" fill="none" stroke={INK} strokeWidth="2.5" />
        <path data-stroke d="M192,424 C380,418 580,430 760,420" fill="none" stroke={INK_LIGHT} strokeWidth="2.5" strokeLinecap="round" />
        <path data-stroke d="M192,452 C230,449 270,455 310,451" fill="none" stroke={INK_LIGHT} strokeWidth="2" strokeLinecap="round" />

        <circle data-stroke cx="118" cy="498" r="8" fill="none" stroke={INK} strokeWidth="2.5" />
        <path data-stroke d="M145,498 C340,490 560,504 770,492" fill="none" stroke={INK} strokeWidth="3" strokeLinecap="round" />

        <path data-stroke d="M100,562 Q112,550 122,562 T145,562" fill="none" stroke={INK_LIGHT} strokeWidth="2.5" strokeLinecap="round" />
        <text data-fade data-fade-after="13" x="163" y="569" fontSize="22" fontWeight="600" fill={INK}>
          tab → task
        </text>

        <path data-stroke d="M372,562 Q384,550 394,562 T417,562" fill="none" stroke={INK_LIGHT} strokeWidth="2.5" strokeLinecap="round" />
        <text data-fade data-fade-after="14" x="435" y="569" fontSize="22" fontWeight="600" fill={INK}>
          backspace → paragraph
        </text>

        <path data-stroke d="M100,645 C280,641 440,649 590,644" fill="none" stroke={INK_LIGHT} strokeWidth="2" strokeLinecap="round" />
        <path data-stroke d="M100,678 C180,675 260,681 345,677" fill="none" stroke={INK_LIGHT} strokeWidth="2" strokeLinecap="round" />
      </svg>
    </div>
  );
};

// Static wireframes for directions that were explored and rejected. Same ink
// palette and cream paper as the "Landed on" sketch, but no animation —
// that effect is reserved for the one that shipped.
const FormField = ({ label, dashed, tall, icon }) => (
  <div className="flex-1 min-w-0">
    <p className="text-xs mb-1" style={{ color: INK_LIGHT }}>
      {label}
    </p>
    <div
      className={`rounded border ${tall ? 'h-12' : 'h-8'} flex items-center ${icon ? 'justify-end px-2' : ''}`}
      style={{ borderColor: INK, borderStyle: dashed ? 'dashed' : 'solid' }}
    >
      {icon && (
        <span className="text-sm leading-none" style={{ color: INK }}>
          +
        </span>
      )}
    </div>
  </div>
);

const StructuredFormWireframe = () => (
  <div
    className="rounded-lg border border-gray-light shadow-lg overflow-hidden p-5"
    style={{ backgroundColor: SKETCH_BG }}
  >
    <div className="space-y-3">
      <FormField label="Title" />
      <FormField label="Description" tall />
      <div className="flex gap-3">
        <FormField label="Owner" />
        <FormField label="Due date" />
      </div>
      <FormField label="Objective" dashed icon />
      <FormField label="Key result 1" />
      <div className="flex gap-3">
        <FormField label="Success metric" />
        <FormField label="Owner" />
      </div>
      <div className="flex items-center gap-2 pt-1">
        <span style={{ color: INK_LIGHT }}>〜</span>
        <span className="text-sm" style={{ color: INK_LIGHT }}>
          add key result
        </span>
      </div>
    </div>
  </div>
);

const WizardScreen = ({ label, button, children }) => (
  <div
    className="rounded-lg border shadow-sm p-3 flex-1 min-w-[110px]"
    style={{ borderColor: INK, backgroundColor: SKETCH_BG }}
  >
    <div className="flex gap-1 mb-3">
      {[0, 1, 2].map((i) => (
        <span key={i} className="w-1.5 h-1.5 rounded-full border" style={{ borderColor: INK_LIGHT }} />
      ))}
    </div>
    <p className="text-xs mb-2" style={{ color: INK }}>
      {label}
    </p>
    {children}
    <div
      className="mt-4 inline-block rounded-full border px-3 py-1 text-xs"
      style={{ borderColor: INK_LIGHT, color: INK_LIGHT }}
    >
      {button}
    </div>
  </div>
);

const WizardArrow = () => (
  <div className="flex items-center px-1 shrink-0" style={{ color: INK_LIGHT }}>
    →
  </div>
);

const WizardWireframe = () => (
  <div className="flex items-stretch gap-2 overflow-x-auto pb-1">
    <WizardScreen label="What's your goal?" button="next">
      <div className="rounded border h-7" style={{ borderColor: INK }} />
    </WizardScreen>
    <WizardArrow />
    <WizardScreen label="Add key results" button="next">
      <div className="rounded border h-7 mb-2" style={{ borderColor: INK }} />
      <div className="rounded border h-7" style={{ borderColor: INK }} />
    </WizardScreen>
    <WizardArrow />
    <WizardScreen label="Link an objective?" button="finish">
      <div className="rounded h-7" style={{ border: `1px dashed ${INK}` }} />
    </WizardScreen>
  </div>
);

// Synthesis diagram: both rejected directions broke apart something people
// already had; the inline, list-based structure kept it whole. Real SVG (not
// an image) so it uses the page's own tokens — gray-light for the rejected
// boxes, the Culture Amp ACCENT for the one that shipped — instead of
// hardcoded colors.
const RejectionSynthesisDiagram = () => (
  <svg
    viewBox="0 0 740 222"
    className="w-full h-auto block"
    role="img"
    aria-label="Structured form and step-by-step wizard both broke apart what people had already written or how they wanted to see it; the inline, list-based structure kept it whole"
  >
    <rect x="20" y="2" width="280" height="90" rx="14" className="fill-gray-light stroke-gray-light" strokeWidth="1" />
    <text x="160" y="40" textAnchor="middle" fontSize="20" fontWeight="700" className="fill-text-primary">
      Structured form
    </text>
    <text x="160" y="66" textAnchor="middle" fontSize="16" className="fill-tertiary">
      Broke apart drafted text
    </text>

    <rect x="20" y="122" width="280" height="90" rx="14" className="fill-gray-light stroke-gray-light" strokeWidth="1" />
    <text x="160" y="160" textAnchor="middle" fontSize="20" fontWeight="700" className="fill-text-primary">
      Step-by-step wizard
    </text>
    <text x="160" y="186" textAnchor="middle" fontSize="16" className="fill-tertiary">
      Broke apart the single view
    </text>

    <path d="M300,47 H380 V95" fill="none" strokeWidth="2" className="stroke-tertiary" />
    <path d="M372,95 L380,103 L388,95" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="stroke-tertiary" />

    <path d="M300,167 H380 V119" fill="none" strokeWidth="2" className="stroke-tertiary" />
    <path d="M372,119 L380,111 L388,119" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="stroke-tertiary" />

    <rect x="440" y="62" width="280" height="90" rx="14" fill="rgba(154, 54, 178, 0.08)" stroke={ACCENT} strokeWidth="1.5" />
    <text x="580" y="100" textAnchor="middle" fontSize="20" fontWeight="700" fill={ACCENT}>
      Inline, list-based text
    </text>
    <text x="580" y="126" textAnchor="middle" fontSize="16" fill={ACCENT}>
      Kept both things whole
    </text>
  </svg>
);

const RejectedDirection = ({ eyebrow, wireframe, description, reason }) => (
  <div className="flex flex-col">
    <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-3">{eyebrow}</p>
    {wireframe}
    <p className="mt-3 text-sm text-tertiary">{description}</p>
    <p className="mt-2 text-sm text-tertiary">
      <strong className="text-text-primary">Why it didn't ship: </strong>
      {reason}
    </p>
  </div>
);

const CultureAmp = () => {
  useNoIndex();
  const IMG = '/images/case-studies/culture-amp';

  return (
    <CaseStudyGate projectName="Culture Amp">
      <div className="min-h-screen bg-primary">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="pt-8">
            <Link
              to="/"
              className="inline-flex items-center text-tertiary hover:text-[#9A36B2] transition-colors duration-300"
            >
              ← Back to Home
            </Link>
          </div>

          {/* Header */}
          <header className="pt-10 pb-16 sm:pt-16 sm:pb-20">
            <img
              src="/images/cultureamp_logo.png"
              alt="Culture Amp"
              className="h-10 sm:h-12 object-contain mb-8"
            />
            <h1 className="text-4xl sm:text-5xl font-bold text-text-primary leading-tight mb-6 max-w-3xl">
              Rearchitecting Culture Amp's goals flow around how people actually write goals.
            </h1>
            <p className="text-tertiary text-sm mb-6"><span className="font-bold">Senior Product Designer</span> · 2023</p>
            <div className="w-16 h-1 mb-8" style={{ backgroundColor: ACCENT }} />
            <div className="grid grid-cols-[2fr_1fr] gap-6 text-sm max-w-3xl">
              <div>
                <p className="text-tertiary mb-1">Team</p>
                <p className="text-text-primary font-medium">
                  I joined this team as the senior designer, partnering with a junior designer on
                  execution while I led the redesign.
                </p>
              </div>
              <div>
                <p className="text-tertiary mb-1">Timeline</p>
                <p className="text-text-primary font-medium">1-month design sprint · 2023</p>
              </div>
              <div>
                <p className="text-tertiary mb-1">Goal</p>
                <p className="text-text-primary font-medium">Simplify goal creation &amp; collaboration</p>
              </div>
              <div>
                <p className="text-tertiary mb-1">Deliverable</p>
                <p className="text-text-primary font-medium">Tested, approved user-flow prototype</p>
              </div>
            </div>
          </header>

          {/* Context */}
          <section className="py-16 sm:py-24 border-t border-gray-light">
            <SectionHeading eyebrow="Context" title="A tool people avoided until the last step" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              <div className="lg:col-span-2 space-y-4 text-tertiary text-lg">
                <p>
                  Culture Amp's Goals product is built around OKRs: org-wide alignment depends on
                  people actually recording their goals and key results in the tool. But research
                  surfaced a workaround: admins, managers, and individual contributors were drafting
                  goals in outside note-taking apps first, then logging into Culture Amp to copy them
                  in one field at a time.
                </p>
                <p>
                  Every one of those extra steps was a chance for a goal to never make it in at all.
                  The creation form itself reinforced the friction: it only allowed one goal or key
                  result at a time, with no way to bring in something already written elsewhere, or
                  connect it to a broader business objective.
                </p>
                <p>
                  I led a one-month design sprint to fix the creation flow itself: synthesizing the
                  research, facilitating cross-team brainstorming, and landing on a tested, approved
                  workflow.
                </p>
              </div>
              <div className="bg-white rounded-lg border border-gray-light p-6 h-fit">
                <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-4">
                  Who it's for
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {['Admin', 'Manager', 'Individual Contributor'].map((persona) => (
                    <span
                      key={persona}
                      className="px-3 py-1 bg-gray-light text-sm font-medium rounded text-tertiary"
                    >
                      {persona}
                    </span>
                  ))}
                </div>
                <p className="text-tertiary text-sm">
                  All employees can create goals. Individual contributors collaborate with managers
                  on team goals, while admins track progress through a dashboard.
                </p>
              </div>
            </div>
          </section>

          {/* Process */}
          <section className="py-16 sm:py-24 border-t border-gray-light">
            <SectionHeading
              eyebrow="Process"
              title="Why the fix was about habits, not fields"
            />

            <div className="mb-16">
              <h3 className="text-xl font-bold text-text-primary mb-2">Key research finding</h3>
              <p className="text-tertiary text-lg mb-8 max-w-2xl">
                Admins, individual contributors, and managers consistently leaned on other note-taking
                apps before ever opening Culture Amp's goals tool.
              </p>
              <blockquote className="border-l-4 pl-6 py-1 mb-8 max-w-2xl" style={{ borderColor: ACCENT }}>
                <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-2">
                  Customer quote
                </p>
                <p className="text-tertiary text-lg italic">
                  "It's really annoying and time consuming to have to create each goal separately and
                  link it to the same objective."
                </p>
              </blockquote>
              <div className="flex flex-col sm:flex-row items-stretch gap-3 sm:gap-0 overflow-x-auto pb-2">
                {[
                  'Gets prompted to create goals or objectives',
                  'Opens note-taking app and begins',
                  'Asks for feedback on goals or objectives',
                  'Copies and pastes goals or objectives one by one into Culture Amp',
                  'Logs in to update checkin',
                ].map((step, i, arr) => (
                  <React.Fragment key={step}>
                    <div
                      className="text-white text-sm font-medium rounded-lg px-4 py-4 flex items-center text-center min-w-[160px] sm:min-w-0 sm:flex-1"
                      style={{ backgroundColor: MAUVE }}
                    >
                      {step}
                    </div>
                    {i < arr.length - 1 && (
                      <div className="flex sm:hidden items-center justify-center text-tertiary">↓</div>
                    )}
                    {i < arr.length - 1 && (
                      <div className="hidden sm:flex items-center px-2 text-tertiary">···&gt;</div>
                    )}
                  </React.Fragment>
                ))}
              </div>
              <p className="text-tertiary mt-4 italic text-sm">
                The tool wasn't where a goal started. It was the last stop for entering one.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              <div>
                <h3 className="text-xl font-bold text-text-primary mb-3">Current problem</h3>
                <p className="text-tertiary text-lg">
                  Goals and key results had to be created one at a time, with no way to enter several at once, so dozens of key results meant repeating the same flow dozens of times.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold text-text-primary mb-3">Hypothesis</h3>
                <p className="text-tertiary text-lg">
                  Reducing the gap between how people already draft goals and how they enter them
                  into Culture Amp would improve speed, alignment, and adoption. The open question
                  was what form that should take.
                </p>
              </div>
            </div>

            <div className="mb-16">
              <h3 className="text-xl font-bold text-text-primary mb-5">Explored and rejected</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <RejectedDirection
                  eyebrow="Structured form"
                  wireframe={<StructuredFormWireframe />}
                  description="A form with every field exposed upfront: title, owner, due date, objective, and key result details before the goal itself was even written."
                  reason="Users draft goals in a note-taking app first, then paste them in. A form with a separate labeled field for every detail meant manually breaking that pasted text apart instead of just dropping it in."
                />
                <RejectedDirection
                  eyebrow="Step-by-step wizard"
                  wireframe={<WizardWireframe />}
                  description="Goal, key results, and objective split across three screens, one field at a time."
                  reason="Users wanted to see, edit, and publish everything in one place, not step through separate screens to get there."
                />
              </div>

              <div className="mt-10 pt-8 border-t border-gray-light">
                <p className="text-xs font-bold uppercase tracking-widest text-tertiary mb-2">
                  Learnings
                </p>
                <p className="text-tertiary text-lg max-w-3xl">
                  Both approaches failed for related reasons: the form broke apart text people had
                  already drafted elsewhere, and the wizard broke apart the single view they wanted
                  to work in. The common thread (don't fragment what people had already written, or
                  how they wanted to see it) pointed toward one field that took text as-is and
                  showed everything at once. That became the inline, list-based structure.
                </p>
                <div className="mt-4 max-w-2xl mx-auto">
                  <RejectionSynthesisDiagram />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-text-primary mb-4">Landed on this UX approach</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                <ul className="space-y-3 text-tertiary text-lg">
                  <li className="flex gap-3">
                    <span style={{ color: ACCENT }}>•</span>
                    Simplify goal creation by removing fields and using inline text.
                  </li>
                  <li className="flex gap-3">
                    <span style={{ color: ACCENT }}>•</span>
                    Allow users to import goals directly from their notes.
                  </li>
                  <li className="flex gap-3">
                    <span style={{ color: ACCENT }}>•</span>
                    Leverage existing habits to reduce cognitive load.
                  </li>
                </ul>
                {/* Paper sketch hidden for now — restore alongside the wireframe when ready.
                <figure>
                  <img
                    src={`${IMG}/sketch-early-exploration.jpg`}
                    alt="Early paper sketches exploring the inline, list-based goal structure"
                    className="w-full h-auto rounded-lg shadow-lg border border-gray-light"
                  />
                  <figcaption className="mt-3 text-sm text-tertiary">
                    Early paper sketches, mapping the inline, list-based structure before touching
                    high fidelity.
                  </figcaption>
                </figure>
                */}
                <figure>
                  <WireframeSketch />
                  <figcaption className="mt-3 text-sm text-tertiary">
                    Translating the sketch into an inline, list-based structure: tab to add a task,
                    backspace to collapse it to a paragraph.
                  </figcaption>
                </figure>
              </div>
            </div>
          </section>

          {/* Iteration */}
          <section className="py-16 sm:py-24 border-t border-gray-light">
            <SectionHeading
              eyebrow="Iteration"
              title="What shipped, and what changed after testing"
              intro="The flow people actually use, plus the two decisions that moved the most between what we tried and what we landed on."
            />

            <div className="mb-20">
              <h3 className="text-xl font-bold text-text-primary mb-6">How it works</h3>
              <div className="grid grid-cols-1 gap-16">
                <div className="max-w-3xl">
                  <Shot
                    step="1"
                    src={`${IMG}/flow-1-goal.webp`}
                    alt="New goal draft with the Link to another goal or OKR search open, and the Get the JOB! objective selected"
                    caption="The goal is entered as a sentence: the same field optionally links it to an existing business objective."
                  />
                </div>

                {/* Step 2: what the user already had drafted, then the same text pasted in */}
                <figure>
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-6 md:gap-8 items-center">
                    <img
                      src={`${IMG}/flow-2-notes.webp`}
                      alt="Key results already drafted as a list in the Notes app"
                      className="w-full h-auto"
                    />
                    <div className="flex justify-center text-3xl leading-none" style={{ color: ACCENT }} aria-hidden="true">
                      <span className="md:hidden">↓</span>
                      <span className="hidden md:inline">→</span>
                    </div>
                    <div className="rounded-lg overflow-hidden shadow-xl border border-gray-light bg-white">
                      <div className="flex items-center gap-1.5 px-3 py-2 bg-gray-light" aria-hidden="true">
                        <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
                        <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
                        <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
                      </div>
                      <img
                        src={`${IMG}/flow-2-pasted.webp`}
                        alt="The same list pasted into the goal, each line becoming a key result with its description beneath"
                        className="w-full h-auto block"
                      />
                    </div>
                  </div>
                  <figcaption className="mt-3 text-sm text-tertiary">
                    <span className="font-bold mr-1.5" style={{ color: ACCENT }}>
                      2
                    </span>
                    Key results get pasted in however they were already drafted: the field just takes
                    text.
                  </figcaption>
                </figure>

                <div className="max-w-3xl">
                  <Shot
                    step="3"
                    src={`${IMG}/flow-3-task.webp`}
                    alt="A description line turned into a task with a checkbox, with the formatting toolbar open above it"
                    caption="Tab creates a task, backspace collapses it to a paragraph: shortcuts modeled on note-taking apps, not a new pattern to learn."
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-text-primary mb-8">Tested vs. landed on</h3>

              <TestedVsLanded
                tested={<KRSettingsTested />}
                testedCaption="Every key result showed its full set of options (due date, success metric, owner, reminder) inline, all the time."
                landed={<KRSettingsLanded />}
                landedCaption="Options collapsed into an overflow menu, surfaced only for the row actually being edited."
                feedback={
                  <>
                    <strong className="text-text-primary">Feedback: </strong>
                    Users found the top-level options under each key result confusing, and usage
                    data showed most settings stayed consistent across goal creation anyway.
                  </>
                }
              />

              <TestedVsLanded
                tested={<DescriptionTested />}
                testedCaption='An optional description field used ghost text ("What are you trying to achieve?") to prompt input.'
                landed={<DescriptionLanded />}
                landedCaption="Ghost text removed. Focus shifted to key-result creation, with an explicit menu for people who don't use keyboard shortcuts."
                feedback={
                  <>
                    <strong className="text-text-primary">Feedback: </strong>
                    Users wanted a non-keyboard way to create key results, and usage data showed most
                    goals shipped without a custom description at all.
                  </>
                }
              />
            </div>

            <div className="mt-20">
              <h3 className="text-xl font-bold text-text-primary mb-2">Shipped alongside it</h3>
              <p className="text-tertiary mb-6 max-w-2xl text-lg">
                Two capabilities that carried the new flow through to the rest of a goal's
                lifecycle, so entering a goal well wasn't the only thing that got easier.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <Shot
                  chrome={false}
                  src={`${IMG}/feature-checkin.png`}
                  alt="Checkin modal in a messaging app"
                  caption="Progress checkins directly in Slack and Microsoft Teams, with automated reminders."
                />
                <Shot
                  src={`${IMG}/feature-progress-detail.png`}
                  alt="Goal progress detail view with chart and history"
                  caption="Admins and managers track high-level progress through expandable dashboard views."
                />
              </div>
            </div>
          </section>

          {/* Outcome */}
          <section className="py-16 sm:py-24 border-t border-gray-light">
            <SectionHeading eyebrow="Outcome" title="Measured after launch" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 mb-6">
              <StatTile value="60%" label="increase in product usage" />
              <StatTile value="80%" label="reduction in time to create a goal, vs. the original one-field-at-a-time form" />
              <StatTile value="✓" label="Simplified flow tested and approved by cross-functional stakeholders before rollout" />
            </div>
          </section>

          <OtherProjects current="cultureAmp" />
        </div>
      </div>
      <Footer />
    </CaseStudyGate>
  );
};

export default CultureAmp;
